import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ChevronLeft, ChevronRight, Flame } from 'lucide-react';
import { PRAYER_LABEL, SALAH, addDays, dayKeyIn } from '@shared/prayer';
import { useProgress, useSettings } from '@/lib/store';
import { Card, IconButton, Section, Segmented, haptic } from '@/components/ui';
import { GROUPS, GROUP_OF, STREAKS, daysIn, fardCount, periodLabel, periodRange, periodStats, shiftPeriod, streakFor, type Period, type StatGroup } from './prayerMetrics';
import { SUNNAH } from './sunnah';
import { periodOn } from '@/features/fasting/fastMetrics';

const GROUP_COLOR: Record<StatGroup | 'none', string> = {
  jamaah: 'var(--st-jamaah)', ontime: 'var(--st-ontime)', late: 'var(--st-late)', missed: 'var(--st-missed)', none: 'var(--st-none)',
};
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
/* Sequential single-hue ramp for "fard prayed that day" (0–5). */
const heat = (n: number) => (n === 0 ? 'var(--st-none)' : `color-mix(in srgb, var(--accent) ${20 + n * 16}%, transparent)`);

function Legend() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 px-4 pt-3 text-[12px] text-muted">
      {GROUPS.map((g) => <span key={g.key} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: GROUP_COLOR[g.key] }} />{g.label}</span>)}
      <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: GROUP_COLOR.none }} />Not logged</span>
    </div>
  );
}

export function PrayerStatsPage() {
  const nav = useNavigate();
  const tz = useSettings((s) => s.settings.prayer.tz);
  const trackSunnah = useSettings((s) => s.settings.trackSunnah);
  const log = useProgress((s) => s.prayers);
  const periods = useProgress((s) => s.periods);
  const gender = useSettings((s) => s.settings.gender);
  const today = dayKeyIn(tz);
  const [period, setPeriod] = useState<Period>('week');
  const [ref, setRef] = useState(today);
  const days = useMemo(() => daysIn(period, ref, today), [period, ref, today]);
  const excused = useMemo(() => (gender === 'female' ? (d: string) => !!periodOn(periods, d, today) : () => false), [gender, periods, today]);
  const stats = useMemo(() => periodStats(log, days, excused), [log, days, excused]);
  const atLatest = periodRange(period, ref).end >= today;
  const openDay = (d: string) => { haptic(12); nav(d === today ? '/prayer' : `/prayer?day=${d}`); };

  return (
    <div className="page-pad">
      <div className="flex items-center gap-2 px-3 pb-3">
        <IconButton label="Back" onClick={() => nav(-1)}><ChevronLeft size={20} /></IconButton>
        <h1 className="text-[28px] font-bold">Prayer stats</h1>
      </div>

      <div className="px-4 pb-3">
        <Segmented label="Period" value={period} onChange={(p) => { setPeriod(p); setRef(today); }}
          options={[{ value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }, { value: 'year', label: 'Year' }]} />
      </div>
      <div className="flex items-center justify-between px-4 pb-4">
        <button type="button" aria-label="Previous period" onClick={() => setRef(shiftPeriod(period, ref, -1))} className="press grid h-9 w-9 place-items-center rounded-full bg-fill"><ChevronLeft size={18} /></button>
        <span className="text-[17px] font-bold">{periodLabel(period, ref)}</span>
        <button type="button" aria-label="Next period" disabled={atLatest} onClick={() => setRef(shiftPeriod(period, ref, 1))} className="press grid h-9 w-9 place-items-center rounded-full bg-fill disabled:opacity-30"><ChevronRight size={18} /></button>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-2 gap-3 px-4 pb-5">
        <Card className="p-4">
          <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">Fard prayed</div>
          <div className="mt-1 text-[32px] font-bold tabular-nums leading-none">{pct(stats.prayed, stats.possible)}%</div>
          <div className="mt-1 text-[13px] text-muted">{stats.prayed} of {stats.possible}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">On time</div>
          <div className="mt-1 text-[32px] font-bold tabular-nums leading-none">{pct(stats.onTime, stats.possible)}%</div>
          <div className="mt-1 text-[13px] text-muted">{stats.jamaah} in jamāʿah</div>
        </Card>
        <Card className="p-4">
          <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">Missed</div>
          <div className="mt-1 text-[32px] font-bold tabular-nums leading-none">{stats.missed}</div>
          <div className="mt-1 text-[13px] text-muted">{stats.possible - stats.logged} not logged{stats.excusedDays ? ` · ${stats.excusedDays} excused` : ''}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">Rawātib days</div>
          <div className="mt-1 text-[32px] font-bold tabular-nums leading-none">{stats.rawatibDays}</div>
          <div className="mt-1 text-[13px] text-muted">all 12 sunnah rakʿahs</div>
        </Card>
      </div>

      <Section title="Streaks" footer="Current streak · your best ever. A streak still counts if today isn't logged yet.">
        <div className="grid grid-cols-2 gap-px bg-hairline">
          {STREAKS.filter((s) => !['witr', 'duha', 'tahajjud'].includes(s.key) || trackSunnah[s.key as 'witr' | 'duha' | 'tahajjud']).map((s) => {
            const { current, best } = streakFor(log, s, today, excused);
            return (
              <div key={s.key} className="flex items-center gap-3 bg-surface px-4 py-3">
                <Flame size={20} className={current ? 'text-[#ff9f0a]' : 'text-faint'} aria-hidden />
                <div className="min-w-0">
                  <div className="text-[20px] font-bold tabular-nums leading-tight">{current} <span className="text-[12px] font-medium text-muted">day{current === 1 ? '' : 's'}</span></div>
                  <div className="truncate text-[13px]">{s.label}</div>
                  <div className="text-[11px] text-muted">best {best}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="By prayer">
        <div className="space-y-3 p-4">
          {SALAH.map((p) => {
            const row = stats.perPrayer[p];
            const total = (row.jamaah + row.ontime + row.late + row.missed + row.none) || 1;
            return (
              <div key={p}>
                <div className="mb-1 flex justify-between text-[14px]">
                  <span className="font-semibold">{PRAYER_LABEL[p].en}</span>
                  <span className="tabular-nums text-muted">{pct(row.jamaah + row.ontime + row.late, total)}% prayed</span>
                </div>
                <div className="flex h-3.5 gap-[2px] overflow-hidden rounded-[4px]" role="img"
                  aria-label={`${PRAYER_LABEL[p].en}: ${GROUPS.map((g) => `${row[g.key]} ${g.label}`).join(', ')}, ${row.none} not logged`}>
                  {([...GROUPS.map((g) => g.key), 'none'] as const).map((k) => row[k] > 0 && (
                    <span key={k} title={`${k === 'none' ? 'Not logged' : GROUPS.find((g) => g.key === k)!.label}: ${row[k]}`}
                      style={{ flexGrow: row[k], background: GROUP_COLOR[k] }} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <Legend />
        <div className="h-4" />
      </Section>

      <Section title={period === 'week' ? 'This week, day by day' : period === 'month' ? 'Calendar' : 'Month by month'}
        footer={period === 'year' ? 'Share of the five daily prayers logged as prayed each month.' : 'Tap a day to see or edit its log.'}>
        {period === 'week' && <WeekGrid days={days} start={periodRange('week', ref).start} log={log} onOpen={openDay} />}
        {period === 'month' && <MonthCalendar start={periodRange('month', ref).start} today={today} log={log} onOpen={openDay} excused={excused} />}
        {period === 'year' && <YearBars year={periodRange('year', ref).start.slice(0, 4)} today={today} log={log} />}
      </Section>

      <Section title="Sunnah prayers" footer="Days you logged each optional prayer in this period.">
        {SUNNAH.filter((s) => trackSunnah[s.key]).map((s) => {
          const v = stats.sunnah[s.key];
          return (
            <div key={s.key} className="row">
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-medium">{s.name}</span>
                <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-fill"><span className="block h-full rounded-full bg-accent" style={{ width: `${pct(v.done, stats.days)}%` }} /></span>
              </span>
              <span className="w-[86px] text-right text-[14px] tabular-nums text-muted">{v.done}/{stats.days} days{v.done && s.options ? <><br />{v.rakats} rakʿahs</> : null}</span>
            </div>
          );
        })}
      </Section>
      <p className="px-8 pb-4 text-center text-[13px]"><Link to="/settings/prayer" className="font-semibold text-accent">Choose which sunnah prayers to track</Link></p>
    </div>
  );
}

function WeekGrid({ days, start, log, onOpen }: { days: string[]; start: string; log: ReturnType<typeof useProgress.getState>['prayers']; onOpen: (d: string) => void }) {
  const all = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="overflow-x-auto p-4">
      <table className="w-full table-fixed text-center text-[12px]">
        <thead>
          <tr><th className="w-[72px]" />{all.map((d) => (
            <th key={d} className="pb-2 font-medium text-muted">
              <button type="button" disabled={!days.includes(d)} onClick={() => onOpen(d)} className="press disabled:opacity-40">
                {new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'narrow', timeZone: 'UTC' })}<br />{Number(d.slice(8))}
              </button>
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {SALAH.map((p) => (
            <tr key={p}>
              <td className="pr-2 text-left text-[13px] font-medium">{PRAYER_LABEL[p].en}</td>
              {all.map((d) => {
                const st = log[d]?.marks[p];
                const g = st ? GROUP_OF[st] : 'none';
                return (
                  <td key={d} className="py-[3px]">
                    <button type="button" disabled={!days.includes(d)} onClick={() => onOpen(d)} title={`${d} ${PRAYER_LABEL[p].en}: ${st ? GROUPS.find((x) => x.key === g)!.label : 'not logged'}`}
                      aria-label={`${d} ${PRAYER_LABEL[p].en}: ${st ? GROUPS.find((x) => x.key === g)!.label : 'not logged'}`}
                      className="mx-auto block h-6 w-6 rounded-[6px] disabled:opacity-30" style={{ background: GROUP_COLOR[g] }} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <Legend />
    </div>
  );
}

function MonthCalendar({ start, today, log, onOpen, excused }: { start: string; today: string; log: ReturnType<typeof useProgress.getState>['prayers']; onOpen: (d: string) => void; excused: (d: string) => boolean }) {
  const lead = (new Date(`${start}T12:00:00Z`).getUTCDay() + 6) % 7;
  const [y, m] = start.split('-').map(Number);
  const len = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const cells = [...Array(lead).fill(null), ...Array.from({ length: len }, (_, i) => addDays(start, i))];
  return (
    <div className="p-4">
      <div className="grid grid-cols-7 gap-1.5 text-center text-[11px] font-medium text-muted">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <span key={i}>{d}</span>)}
      </div>
      <div className="mt-1.5 grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => {
          if (!d) return <span key={`x${i}`} />;
          const n = fardCount(log[d]);
          const future = d > today;
          return (
            <button key={d} type="button" disabled={future} onClick={() => onOpen(d)} title={`${d}: ${n}/5 prayed`} aria-label={`${d}: ${n} of 5 prayed`}
              className={`press flex aspect-square flex-col items-center justify-center rounded-[8px] text-[12px] font-semibold disabled:opacity-25 ${d === today ? 'ring-2 ring-accent' : ''}`}
              style={{ background: excused(d) && n === 0 ? 'color-mix(in srgb, #e87ba4 22%, transparent)' : heat(n) }}>
              <span>{Number(d.slice(8))}</span>
              {n > 0 ? <span className="text-[9px] font-medium opacity-80">{n}/5</span> : excused(d) ? <span className="text-[9px] font-medium opacity-80">excused</span> : null}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-end gap-1.5 text-[11px] text-muted">
        0 {[0, 1, 2, 3, 4, 5].map((n) => <span key={n} className="h-3 w-3 rounded-[3px]" style={{ background: heat(n) }} />)} 5 prayed
      </div>
    </div>
  );
}

function YearBars({ year, today, log }: { year: string; today: string; log: ReturnType<typeof useProgress.getState>['prayers'] }) {
  const months = Array.from({ length: 12 }, (_, i) => {
    const start = `${year}-${String(i + 1).padStart(2, '0')}-01`;
    const days = daysIn('month', start, today);
    const s = periodStats(log, days);
    return { label: new Date(`${start}T12:00:00Z`).toLocaleDateString('en-GB', { month: 'narrow', timeZone: 'UTC' }), full: new Date(`${start}T12:00:00Z`).toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' }), v: s.possible ? s.prayed / s.possible : 0, s, future: !days.length };
  });
  const H = 120;
  return (
    <div className="p-4">
      <div className="flex items-end justify-between gap-1.5" style={{ height: H + 36 }} role="img" aria-label={`Monthly prayer completion for ${year}`}>
        {months.map((mo) => (
          <div key={mo.full} className="flex flex-1 flex-col items-center gap-1" title={`${mo.full}: ${Math.round(mo.v * 100)}% (${mo.s.prayed}/${mo.s.possible})`}>
            <span className="text-[10px] tabular-nums text-muted">{mo.future ? '' : `${Math.round(mo.v * 100)}`}</span>
            <div className="flex w-full items-end rounded-t-[4px] bg-[var(--st-none)]" style={{ height: H }}>
              <div className="w-full rounded-t-[4px] bg-accent" style={{ height: `${mo.v * 100}%`, opacity: mo.future ? 0 : 1 }} />
            </div>
            <span className="text-[11px] font-medium text-muted">{mo.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
